import { createFileRoute, Link } from "@tanstack/react-router";
import { CtaLink } from "@/components/marketing/CtaLink";
import {
  ShieldCheck,
  Lock,
  Database,
  KeyRound,
  Cpu,
  EyeOff,
  Users,
  Activity,
  FileText,
  Sparkles,
  ArrowRight,
  ArrowUpRight,
  Check,
} from "lucide-react";
import { SiteLayout } from "@/components/marketing/SiteLayout";
import { alternateLinkList } from "@/lib/i18n/locale";

export const Route = createFileRoute("/security")({
  head: () => ({
    meta: [
      { title: "Security & Data Protection — banana.cards" },
      { name: "description", content: "Encryption at rest and in transit, privacy-first AI, no data sale. How Banana Cards keeps your data safe by design." },
      { property: "og:title", content: "Security — banana.cards" },
      { property: "og:description", content: "Your safety, our priority. Banana Cards is protected by design." },
    ],
    links: alternateLinkList("/security", "en"),
  }),
  component: SecurityPage,
});

const PROTECTIONS = [
  { icon: Database, title: "PostgreSQL infra", desc: "Secure, battle-tested database foundation." },
  { icon: Lock, title: "AES-256 at rest", desc: "Your data is encrypted while stored." },
  { icon: ShieldCheck, title: "TLS in transit", desc: "All traffic encrypted end-to-end." },
  { icon: KeyRound, title: "Controlled access", desc: "Row-level security and least privilege." },
  { icon: Users, title: "Role-based perms", desc: "Granular team and admin controls." },
  { icon: Cpu, title: "Privacy-first AI", desc: "Enterprise-grade Vertex AI processing." },
  { icon: EyeOff, title: "Not used for training", desc: "Customer data never trains AI models." },
  { icon: Activity, title: "Continuous monitoring", desc: "Active checks for misuse and abuse." },
];

const SECTIONS = [
  {
    id: "database",
    icon: Database,
    title: "Database Security",
    body: [
      "Banana Cards uses Supabase for database management and backend data handling. Supabase is built on PostgreSQL and supports security controls such as Row Level Security, which allows access rules to be applied directly at database level so users can only access data they are authorized to access.",
      "Supabase encrypts customer data at rest using AES-256 and in transit using TLS. Where applicable, Banana Cards layers additional database-level access controls, authentication rules, role-based permissions, and secure backend processes to protect user data against unauthorized access.",
    ],
  },
  {
    id: "ai",
    icon: Cpu,
    title: "AI Processing",
    body: [
      "Banana Cards uses Google Vertex AI to power selected AI features, including contact enrichment, message drafting, profile assistance, business summaries, smart suggestions, and workflow support.",
      "Google's Vertex AI service terms state that Google will not use customer data to train or fine-tune AI/ML models without the customer's prior permission or instruction.",
      "When Banana Cards uses Vertex AI for enterprise-grade AI processing, user data submitted for AI assistance is processed to provide the requested feature — and is not used by Google to train its foundation models unless explicitly permitted.",
    ],
  },
  {
    id: "no-sale",
    icon: EyeOff,
    title: "No Data Sale Policy",
    body: [
      "Banana Cards has a strict No Data Sale Policy. We do not sell user data, contact data, customer records, business profiles, uploaded documents, communication history, or AI-processed information to advertisers, data brokers, or third parties.",
      "User data is used only to provide, secure, improve, and support the Banana Cards platform and related services, subject to our Terms of Service, Privacy Policy, and applicable laws.",
    ],
  },
  {
    id: "access",
    icon: KeyRound,
    title: "Access Control",
    body: [
      "Access to user data is limited to authorized systems, service providers, and personnel where necessary to operate, maintain, secure, or support the platform.",
      "Banana Cards may apply different levels of access depending on the user account type, team permissions, admin settings, and operational requirements.",
      "Users are responsible for maintaining the security of their own accounts, including login credentials, connected email accounts, uploaded contacts, team member access, and shared profile links.",
    ],
  },
  {
    id: "responsibility",
    icon: FileText,
    title: "Data Handling & User Responsibility",
    body: [
      "Users are responsible for ensuring that any contact details, documents, business cards, files, or third-party information uploaded to Banana Cards are collected and processed lawfully.",
      "Users should only upload or process personal data where they have consent, a legitimate business relationship, contractual basis, or another lawful basis under applicable privacy laws.",
      "Banana Cards must not be used to store, process, or share illegally obtained data, scraped personal data, spam lists, purchased contact databases, or unauthorized private information.",
    ],
  },
  {
    id: "limits",
    icon: ShieldCheck,
    title: "Security Limitations",
    body: [
      "While Banana Cards applies reasonable technical, organizational, and operational safeguards, no online platform can guarantee absolute security.",
      "Users should avoid uploading unnecessary sensitive information and should keep account access limited to trusted team members.",
      "Banana Cards may suspend, restrict, or remove access where activity appears suspicious, abusive, unlawful, or in breach of our policies.",
    ],
  },
];

const HIGHLIGHTS = [
  "PostgreSQL-based secure database infrastructure",
  "AES-256 encryption for data at rest",
  "TLS encryption for data in transit",
  "Controlled account access",
  "Role-based team permissions",
  "Privacy-first AI processing",
  "Customer data is not used to train or fine-tune AI/ML models",
  "No sale of user or contact data",
  "Responsible handling of contacts, profiles, files, and business information",
  "Ongoing monitoring to help prevent misuse or suspicious activity",
];

function SecurityPage() {
  return (
    <SiteLayout>
      {/* HERO — black accent section */}
      <section className="relative overflow-hidden bg-foreground text-background">
        {/* background decoration */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              "linear-gradient(to right, currentColor 1px, transparent 1px), linear-gradient(to bottom, currentColor 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -top-32 right-1/2 h-[480px] w-[480px] translate-x-1/2 rounded-full bg-brand-lime/30 blur-[120px]"
          aria-hidden
        />

        <div className="relative mx-auto grid max-w-7xl gap-16 px-4 pb-20 pt-24 md:grid-cols-[1.1fr_1fr] md:px-6 md:pt-28">
          <div className="flex flex-col justify-center">
            <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-background/60">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-brand-lime" />
              Security
            </p>
            <h1 className="mt-3 font-display text-4xl leading-[1.05] md:text-6xl lg:text-7xl">
              Your safety… <em className="italic text-brand-lime">our priority.</em>
            </h1>
            <p className="mt-6 max-w-xl text-base text-background/75 md:text-lg">
              At Banana Cards, your data is protected by design. From digital profiles and saved contacts to files,
              business information, and AI-powered tools — we apply secure data handling practices to keep your
              information safe.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/privacy"
                className="inline-flex items-center gap-2 rounded-xl bg-brand-lime px-6 py-3 text-sm font-bold text-brand-lime-foreground transition hover:scale-[1.02]"
              >
                Privacy policy
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 rounded-xl bg-background/10 px-6 py-3 text-sm font-bold text-background backdrop-blur transition hover:bg-background/20"
              >
                Report an issue
              </Link>
            </div>

            <dl className="mt-12 grid max-w-md grid-cols-3 gap-6 border-t border-background/15 pt-6">
              <div>
                <dt className="font-display text-3xl leading-none">256</dt>
                <dd className="mt-1 text-[11px] font-semibold uppercase tracking-wider text-background/60">AES bit encryption</dd>
              </div>
              <div>
                <dt className="font-display text-3xl leading-none">0</dt>
                <dd className="mt-1 text-[11px] font-semibold uppercase tracking-wider text-background/60">Data sold, ever</dd>
              </div>
              <div>
                <dt className="font-display text-3xl leading-none">24/7</dt>
                <dd className="mt-1 text-[11px] font-semibold uppercase tracking-wider text-background/60">Monitoring</dd>
              </div>
            </dl>
          </div>

          {/* Decorative shield graphic */}
          <div className="relative hidden md:flex md:items-center md:justify-center">
            <div className="relative h-[420px] w-[420px]">
              {/* concentric rings */}
              <div className="absolute inset-0 rounded-full border border-background/10" />
              <div className="absolute inset-8 rounded-full border border-background/15" />
              <div className="absolute inset-16 rounded-full border border-background/20" />
              <div className="absolute inset-24 rounded-full border border-background/30" />

              {/* orbiting chips */}
              <div className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2">
                <Chip icon={Lock} label="AES-256" />
              </div>
              <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2">
                <Chip icon={Database} label="PostgreSQL" />
              </div>
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2">
                <Chip icon={Cpu} label="Vertex AI" />
              </div>
              <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2">
                <Chip icon={KeyRound} label="RLS" />
              </div>

              {/* center shield */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="flex h-32 w-32 items-center justify-center rounded-3xl bg-brand-lime text-brand-lime-foreground shadow-[0_0_80px_oklch(0.96_0.21_117/0.4)]">
                  <ShieldCheck className="h-14 w-14" strokeWidth={2.2} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROTECTIONS GRID — white */}
      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-4 py-20 md:px-6">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Built with protection in mind</p>
            <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
              Eight layers of <em className="italic">peace of mind.</em>
            </h2>
            <p className="mt-4 text-base text-muted-foreground">
              Encryption, controlled access, privacy-first AI, and a strict no-sale stance — woven into every part of the
              platform.
            </p>
          </div>

          <div className="mt-12 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {PROTECTIONS.map((p) => {
              const Icon = p.icon;
              return (
                <div key={p.title} className="rounded-2xl bg-muted p-6">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-card">
                    <Icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-5 text-base font-bold">{p.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{p.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PILLARS — muted */}
      <section className="bg-muted">
        <div className="mx-auto max-w-7xl px-4 py-20 md:px-6">
          <div className="grid gap-6 md:grid-cols-3">
            <Pillar
              icon={Lock}
              title="Encrypted everywhere"
              desc="AES-256 at rest, TLS in transit. Your data is protected whether it's stored or moving between systems."
            />
            <Pillar
              icon={Cpu}
              title="Privacy-first AI"
              desc="We use enterprise-grade Vertex AI. Customer data is never used to train or fine-tune AI/ML models."
              featured
            />
            <Pillar
              icon={EyeOff}
              title="No data sale. Period."
              desc="We don't sell user data, contacts, profiles, files, or AI-processed information to anyone. Ever."
            />
          </div>
        </div>
      </section>

      {/* HIGHLIGHTS BANNER — white */}
      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-4 py-20 md:px-6">
          <div className="grid gap-12 md:grid-cols-2">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">At a glance</p>
              <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
                Everything we ship <em className="italic">to keep you safe.</em>
              </h2>
              <p className="mt-5 max-w-md text-base leading-relaxed text-muted-foreground">
                A short, plain-language summary of the controls, policies, and practices that protect your account and
                your data on Banana Cards.
              </p>
            </div>
            <ul className="grid gap-3 self-center sm:grid-cols-2">
              {HIGHLIGHTS.map((h) => (
                <li key={h} className="flex items-start gap-2 rounded-xl bg-muted p-4 text-sm">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-foreground" />
                  <span className="font-semibold">{h}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* DETAILED POLICY SECTIONS — muted, with TOC */}
      <section className="bg-muted">
        <div className="mx-auto max-w-6xl px-4 py-20 md:px-6">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">The fine print</p>
            <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
              The details, <em className="italic">made readable.</em>
            </h2>
            <p className="mt-4 text-base text-muted-foreground">
              Our platform and software services are operated globally by Six Studio Creative FZCO. In Vietnam, physical
              card services and local operations are managed by Banana Cards Vietnam.
            </p>
          </div>

          <div className="mt-12 grid gap-10 md:grid-cols-[200px_1fr]">
            {/* TOC */}
            <aside className="md:sticky md:top-24 md:self-start">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Sections</p>
              <nav className="mt-4 grid gap-1.5 border-l border-foreground/10 pl-4">
                {SECTIONS.map((s, i) => (
                  <a
                    key={s.id}
                    href={`#${s.id}`}
                    className="text-sm font-medium text-muted-foreground transition hover:text-foreground"
                  >
                    <span className="mr-2 text-muted-foreground/50">{String(i + 1).padStart(2, "0")}</span>
                    {s.title}
                  </a>
                ))}
              </nav>
            </aside>

            {/* Body cards */}
            <div className="space-y-4">
              {SECTIONS.map((s, i) => {
                const Icon = s.icon;
                return (
                  <article
                    key={s.id}
                    id={s.id}
                    className="scroll-mt-24 rounded-3xl bg-card p-6 md:p-10"
                  >
                    <div className="flex items-start gap-4">
                      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-muted">
                        <Icon className="h-5 w-5" />
                      </span>
                      <div>
                        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
                          Section {String(i + 1).padStart(2, "0")}
                        </p>
                        <h3 className="mt-1 font-display text-2xl leading-tight md:text-3xl">{s.title}</h3>
                      </div>
                    </div>
                    <div className="mt-6 space-y-4 text-[15px] leading-relaxed text-foreground/85">
                      {s.body.map((p, idx) => (
                        <p key={idx}>{p}</p>
                      ))}
                    </div>
                  </article>
                );
              })}

              {/* Related */}
              <div className="grid gap-3 pt-4 sm:grid-cols-3">
                {[
                  { label: "Privacy Policy", to: "/privacy" },
                  { label: "Terms of Service", to: "/terms" },
                  { label: "Legal Guidelines", to: "/legal-guidelines" },
                ].map((r) => (
                  <Link
                    key={r.to}
                    to={r.to}
                    className="group flex items-center justify-between gap-4 rounded-2xl bg-card p-5 transition hover:bg-foreground hover:text-background"
                  >
                    <span className="text-sm font-bold">{r.label}</span>
                    <ArrowUpRight className="h-4 w-4 transition group-hover:rotate-12" />
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* OUTRO CTA — black accent */}
      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-4 pb-24 pt-4 md:px-6">
          <div className="relative overflow-hidden rounded-3xl bg-foreground px-8 py-16 text-center text-background md:p-20">
            <div
              className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,oklch(0.96_0.21_117/0.18),transparent_55%)]"
              aria-hidden
            />
            <Sparkles className="mx-auto h-6 w-6 text-brand-lime" />
            <h2 className="mx-auto mt-5 max-w-2xl font-display text-3xl leading-[1.1] md:text-5xl">
              Connect with <em className="italic text-brand-lime">confidence.</em>
            </h2>
            <p className="mx-auto mt-5 max-w-lg text-base text-background/70">
              Manage your business relationships knowing your data is handled securely, responsibly, and with privacy at
              its core.
            </p>
            <div className="relative mt-8 flex flex-wrap items-center justify-center gap-3">
              <CtaLink
                to="/signup"
                className="inline-flex items-center gap-2 rounded-xl bg-brand-lime px-6 py-3 text-sm font-bold text-brand-lime-foreground"
              >
                Get started — free
              </CtaLink>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 rounded-xl border border-background/20 px-6 py-3 text-sm font-bold"
              >
                Talk to security
              </Link>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}

function Chip({ icon: Icon, label }: { icon: typeof Lock; label: string }) {
  return (
    <div className="flex items-center gap-2 rounded-full bg-background px-3 py-1.5 text-foreground shadow-[0_0_24px_oklch(0_0_0/0.4)]">
      <Icon className="h-3.5 w-3.5" />
      <span className="text-[11px] font-bold uppercase tracking-wider">{label}</span>
    </div>
  );
}

function Pillar({
  icon: Icon,
  title,
  desc,
  featured,
}: {
  icon: typeof Lock;
  title: string;
  desc: string;
  featured?: boolean;
}) {
  return (
    <div
      className={`rounded-3xl p-8 ${
        featured ? "bg-foreground text-background ring-2 ring-foreground" : "bg-card"
      }`}
    >
      <span
        className={`flex h-12 w-12 items-center justify-center rounded-xl ${
          featured ? "bg-brand-lime text-brand-lime-foreground" : "bg-muted"
        }`}
      >
        <Icon className="h-5 w-5" />
      </span>
      <h3 className="mt-6 font-display text-xl md:text-2xl">{title}</h3>
      <p className={`mt-3 text-sm leading-relaxed ${featured ? "text-background/75" : "text-muted-foreground"}`}>
        {desc}
      </p>
    </div>
  );
}